import type { Auth0ContextInterface } from '@auth0/auth0-react';
import createAuth0Client, {
  User as Auth0User,
  GetTokenSilentlyOptions,
  IdToken,
  Auth0ClientOptions,
} from '@auth0/auth0-spa-js';
import { QueryClient } from '@tanstack/react-query';
import bhiveIconShort from 'Assets/images/bhive-logo-short.svg';
import bhiveIcon from 'Assets/images/bhive-logo.svg';
import autoLogOut from 'Assets/images/communicator-notification-right-50x50-01.ico';
import googleIconShort from 'Assets/images/google-short.svg';
import googleIcon from 'Assets/images/google.svg';
import iCloudIconShort from 'Assets/images/icloud-short.png';
import iCloudIcon from 'Assets/images/icloud.svg';
import linkedinIcon from 'Assets/images/linkedin.svg';
import microsoftIconShort from 'Assets/images/microsoft-icon-short.svg';
import microsoftIcon from 'Assets/images/microsoft-icon.svg';
import outLookIconShort from 'Assets/images/outlook-short.svg';
import outLookIcon from 'Assets/images/outlook.svg';
import salesforceIcon from 'Assets/images/salesforce.svg';
import type { Identity } from 'Components/Settings/SocialAccounts/types';
import { CONTACTS_PER_PAGE } from 'Constants/contacts';
import {
  API_ENDPOINTS,
  AUTH0,
  AUTH0_CONNECTION,
  AUTH0_MANAGEMENT_API_SCOPE,
  AUTO_LOGOUT_NOTIFICATION_DELAY,
  BASE_CONTACT_API_V2,
  BV_ENV,
  IS_ELECTRON,
  MSG_API_BASE_URI,
  NODE_ENV,
  NODE_ENV_TEST,
  PORTAL_URL,
  REDIRECT_URL,
  WAKEUP_INTERVAL,
  WAKEUP_TIMEOUT,
} from 'Constants/env';
import { POLLING } from 'Constants/featureFlags';
import {
  SIGN_IN_RESPONSE,
  STORE_CHAT_HTML,
  STORE_CHAT_RAW,
} from 'Constants/localstorage';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { AxiosResponseT } from 'Interfaces/axiosResponse';
import { IContactApiPagination } from 'Interfaces/contactApiPagination';
import { LDClient } from 'launchdarkly-js-client-sdk';
import * as localforage from 'localforage';
import { get, has, isEmpty, omitBy } from 'lodash';
import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
  when,
} from 'mobx';
import type { IReactionDisposer, ObservableMap } from 'mobx';
import type { IPromiseBasedObservable } from 'mobx-utils';
import { createTransformer, fromPromise, now } from 'mobx-utils';
import {
  ContactModelV2,
  IAvatarUploadResponse,
  IContact as IContactV2,
  IFormContact,
  TSourcePlatform,
} from 'Models/ContactModelV2';
import {
  IPersonPicture,
  IPersonSignInTokenResponse,
  IPersonVideoFeature,
  PersonModel,
} from 'Models/PersonModel';
import {
  fetchConversations,
  QUERY_KEY_FETCH_CONVERSATIONS,
} from 'Modules/conversation/conversations-list/requests/index.requests';
import { useNotificationStore } from 'Modules/notification/store';
import {
  QUERY_KEY_CHECK_AVAILABILITY_PHONENUMBER,
  fetchAvailabilityPhoneNumberQuery,
} from 'Modules/person/index.requests';
import { usePersonStore } from 'Modules/person/index.store';
import type { InboundNumber } from 'Modules/person/index.types';
import moment from 'moment-timezone';
import queryString from 'query-string';
import type { DropdownItemProps } from 'semantic-ui-react';
import { TokenExchangeScheduleService } from 'Services/tokenExchangeScheduleService';
import { BaseStore } from 'Stores/BaseStore';
import { RootStore } from 'Stores/RootStore';
import { isNullOrUndefined } from 'util';
import {
  pushToGTMDataLayer,
  setAnalyticsUserProperties,
} from 'Utils/analytics';
import {
  getTokenExchangeClassification,
  isAuthRequiredError,
  isOfflineError,
} from 'Utils/auth0ErrorClassification';
import {
  identifyClarityUserId,
  terminateClaritySession,
} from 'Utils/clarityUtils';
import { retryWithBackoff } from 'Utils/exponentialBackoff';
import {
  handleCodeReceived,
  sendIpcIdentify,
  sendIpcLog,
  sendIpcLoginUrl,
  sendIpcSaveCredentials,
  sendIpcUnreadCounts,
} from 'Utils/ipcRendererEvents';
import { loginTokenIsInHash } from 'Utils/login';
import { bugsnagClient } from 'Utils/logUtils';
import {
  formatNumberWithNationalCode,
  isPhoneNumberMatch,
} from 'Utils/phoneUtil';
import { resizeImage } from 'Utils/resizeImage';
import API, {
  AUTH0_API,
  PURE_API,
  getAuthToken,
  removeAxiosAuthHeaders,
  setAPIHeader,
} from '../api';
import { MessagesGetRequest } from '../interfaces/apiDtos';
import { ISource, SourceModelV2 } from '../models/SourceModelV2';
import * as intercom from '../utils/intercomWrapper';
import { isTokenAboutToExpire, parseJwt } from '../utils/jwtUtils';
import { IWakeUpTickReactionData } from './UiStore';

const providerConfig: Auth0ClientOptions = {
  ...AUTH0,
  client_id: AUTH0.clientId,
  redirect_uri: window.location.origin,
  connection: undefined,
  // Note: useRefreshTokens and cacheLocation not needed here since this client
  // is ephemeral - created, used once for ID token, then discarded.
};
if (AUTH0_CONNECTION) {
  providerConfig.connection = AUTH0_CONNECTION;
}

/**
 * Buffer in milliseconds for refreshing token when About to Expire (ATE).
 */
const ATE_REFRESH_BUFFER_MS = 60000;

type ExchangeForBVTokenOptions = GetTokenSilentlyOptions & {
  suppressLogoutOnAuthFailure?: boolean;
};

export class PersonStore extends BaseStore {
  #queryClient: QueryClient;
  private readonly tokenExchangeScheduleService =
    TokenExchangeScheduleService.getInstance(this.rootStore);

  constructor(rootStore: RootStore, queryClient: QueryClient) {
    super(rootStore);
    this.#queryClient = queryClient;
    makeObservable(this);
  }

  private wakeUpCheckTick: IReactionDisposer = null;
  @observable
  lastWakeUpCheck: number = now(WAKEUP_INTERVAL); // 20000

  @observable public fileUploadedS3 = new Map<string, IPersonPicture>();

  @action
  setLastWakeUpCheck = (lastWakeUpCheck: number) =>
    (this.lastWakeUpCheck = lastWakeUpCheck);

  startWakeUpCheckTick = () => {
    this.wakeUpCheckTick = reaction(
      () =>
        ({
          // All calls to `now(WAKEUP_INTERVAL)` are synchronized to `WAKEUP_INTERVAL`
          wakeUpHeartBeat: now(WAKEUP_INTERVAL), // 10000 + (login within) WAKEUP_INTERVAL + (fireImmediately = false, delay until next interval tick) WAKEUP_INTERVAL
          isLoggedIn: this.rootStore.personStore.IsLoggedIn,
        } as IWakeUpTickReactionData),
      (tickData) => {
        if (!tickData.isLoggedIn) {
          return;
        }
        if (
          tickData.wakeUpHeartBeat >
            this.lastWakeUpCheck /* 20000 on first cycle */ +
              WAKEUP_TIMEOUT * 2 &&
          !this.rootStore.personStore.firstLogin
        ) {
          console.log('onWake: Heartbeat  missed');
          window.addEventListener('focus', this.checkOnlineStatus);
        }
        this.setLastWakeUpCheck(now(WAKEUP_INTERVAL));
      },
      {
        name: 'wakeUpCheck',
        fireImmediately: false,
        delay: 1000,
      }
    );
  };

  checkOnlineStatus = () => {
    console.log('onWake: checkOnlineStatus');
    window.removeEventListener('focus', this.checkOnlineStatus);
    if (navigator.onLine) {
      console.log('onWake: Already online');
    } else {
      window.addEventListener('online', this.reloadApp);
    }
  };

  reloadApp = () => {
    sendIpcLog('info', 'Window was automatically reloaded', {
      reason: 'missed heartbeat',
    });
    console.log('onWake: Reloading app');
    window.removeEventListener('online', this.reloadApp);
    bugsnagClient.notify(
      'App was explicitly reloaded due to a missed heartbeat',
      (event) => {
        event.severity = 'info';
      }
    );
    window.location.reload();
  };

  @observable
  addingNewContact = false;

  @action
  setAddingNewContact = (state: boolean) => (this.addingNewContact = state);

  @observable
  hasMoreContacts = false;

  @action
  setHasMoreContacts = (hasMoreContacts: boolean) =>
    (this.hasMoreContacts = hasMoreContacts);

  @observable
  isAddingContact = false;

  @action
  setIsAddingContact = (state: boolean) => (this.isAddingContact = state);

  @observable
  hasContacts = false;

  @action
  setHasContact = (state: boolean) => (this.hasContacts = state);

  @observable
  editContactDetails: ContactModelV2 = null;

  @action
  setEditContact = (contact: ContactModelV2) =>
    (this.editContactDetails = contact);

  @observable
  contactFilterValue: DropdownItemProps = null;

  @action
  setContactFilterValue = (value: DropdownItemProps) =>
    (this.contactFilterValue = value);

  @observable
  loadingContacts = false;

  @action
  setLoadingContact = (state: boolean) => (this.loadingContacts = state);

  @observable
  loadingInfinite = false;

  @action
  setLoaderInfinite = (state: boolean) => (this.loadingInfinite = state);

  @observable
  contactSearchTerm = '';

  @action
  setContactSearchTerm = (term: string) => (this.contactSearchTerm = term);

  @observable
  public loginTokenResponse: IPromiseBasedObservable<IPersonSignInTokenResponse>;

  @observable
  public allContactsTopBar: ContactModelV2[] = observable.array();

  @observable
  public allContacts: ContactModelV2[] = observable.array();

  @observable
  public personsById: ObservableMap<
    string,
    IPromiseBasedObservable<ContactModelV2>
  > = observable.map();

  @observable public loginStatus: IPromiseBasedObservable<
    AxiosResponseT<IPersonSignInTokenResponse>
  > = null;

  @observable public allSources: SourceModelV2[] = observable.array();

  @observable
  loginPageEmail = '';

  @action
  setLoginPageEmail(email: string) {
    this.loginPageEmail = email;
  }

  @observable
  loginPagePassword = '';

  @action
  setLoginPagePassword(password: string) {
    this.loginPagePassword = password;
  }

  /**
   List for info sidebar only if clicked on contact,
   this list holds correspondence contacts by phone number
   */
  @observable
  listOfAllContactsWithNumber: ObservableMap<
    string,
    IPromiseBasedObservable<ContactModelV2[]>
  > = observable.map();

  @observable
  contactPageNumber = 1;

  @action
  setContactPageNumber = (pageNumber: number) =>
    (this.contactPageNumber = pageNumber);

  @observable
  ipcCredentialsEmail = '';

  @action
  setIpcCredentialsEmail(email: string) {
    this.ipcCredentialsEmail = email;
  }

  @observable
  ipcCredentialsPassword = '';

  @action
  setIpcCredentialsPassword(password: string) {
    this.ipcCredentialsPassword = password;
  }

  /** True if the login process is currently running (waiting on Promise resolutions) */
  @observable
  public isLoggingIn = false;

  @action
  setIsLoggingIn = (isLoggingIn: boolean) => (this.isLoggingIn = isLoggingIn);

  @observable
  isEditingContact = false;

  @action
  setIsEditingContact = (state: boolean) => (this.isEditingContact = state);

  @observable
  showEditingLeavePopup: { show: boolean; wantedUrl: string } = {
    show: false,
    wantedUrl: '',
  };

  @action
  setEditingLeavePopup = (state: { show: boolean; wantedUrl: string }) =>
    (this.showEditingLeavePopup = state);

  /** `accountId` of the logged-in `Person` */
  @observable
  public loggedInAccountId = -1;

  /** `id` of the `Person` currently logged in. Value is -1 if not logged in. */
  @observable
  public loggedInPersonId = -1;

  /** `locationId` of the logged-in `Person` */
  @observable
  public loggedInLocationId = 0;

  @observable
  public loggedInEmail = '';

  @observable
  public loggedInPersonRole = '';

  @observable
  public personAvaliableFeatures: {
    video: IPersonVideoFeature;
    socialAccounts: boolean;
  } = {
    video: { enabled: false, expirationDate: '' },
    socialAccounts: false,
  };

  @observable
  newContactErrors: { show: boolean; message: string; field: string } = {
    show: false,
    message: '',
    field: '',
  };

  @action
  setErrorNewContact = (value: {
    show: boolean;
    message: string;
    field: string;
  }) => {
    this.newContactErrors = value;
    const timeout = setTimeout(() => {
      runInAction(
        () => (this.newContactErrors = { show: false, message: '', field: '' })
      );
      clearTimeout(timeout);
    }, 3000);
  };

  @observable
  addedNewSource: {
    show: boolean;
    sourceProvider: string;
    id?: number;
    alreadyAddedMessage?: string;
  } = { show: false, sourceProvider: '' };

  @action
  setAddedNewSource = (value: {
    show: boolean;
    sourceProvider: string;
    id?: number;
    alreadyAddedMessage?: string;
  }) => (this.addedNewSource = value);

  @observable
  firstLogin: boolean;

  @observable
  isAutoLogOut = false;

  @action
  setIsAutoLogout = (isAutoLogOut: boolean) => {
    this.isAutoLogOut = isAutoLogOut;
  };

  @action
  setFirstLogin = (firstLogin: boolean) => (this.firstLogin = firstLogin);

  @computed get IsLoggedIn() {
    return this.loggedInPersonId > 0;
  }

  @observable public isLoggingOut = false;
  @action
  setIsLoggingOut = (isLoggingOut: boolean) =>
    (this.isLoggingOut = isLoggingOut);

  @observable
  public auth0: Auth0ContextInterface<Auth0User> = null;

  @action
  setAuth0 = (auth0: Auth0ContextInterface<Auth0User>) => (this.auth0 = auth0);

  @observable
  public ldClient: LDClient | null = null;

  @action
  setLDClient(ldClient: LDClient) {
    this.ldClient = ldClient;
  }

  /**
   * Set the logged in Person's auth info.
   */
  @action
  setLoggedInInfo = (signInResp: IPersonSignInTokenResponse) => {
    this.loggedInAccountId = signInResp.account.id;
    this.loggedInPersonId =
      typeof signInResp.id === 'string'
        ? parseInt(signInResp.id, 10)
        : signInResp.id;
    this.loggedInEmail = signInResp.email;
    this.loggedInLocationId = signInResp.location.id;
    setAPIHeader('Authorization', `Bearer ${signInResp.token}`);

    // id, email and name must be set with setUser(), remaining props with addMetadata('user', {})
    bugsnagClient.setUser(
      `${this.loggedInAccountId}:${this.loggedInPersonId}`,
      signInResp.email,
      `${signInResp.firstName} ${signInResp.lastName}`
    );
    bugsnagClient.addMetadata('user', {
      personId: this.loggedInPersonId,
      accountId: this.loggedInAccountId,
      package: signInResp.package,
      locationId: signInResp.locationId,
    });

    bugsnagClient.addMetadata('custom', {
      nodeEnv: NODE_ENV,
      bvEnv: BV_ENV,
      apiUrl: MSG_API_BASE_URI,
    });

    // @ts-ignore
    if (window.document?.wasDiscarded) {
      bugsnagClient.notify('Discarded Tab', (event) => {
        event.severity = 'info';
      });
    }

    setAnalyticsUserProperties({
      userId: signInResp.id,
      accountId: signInResp.account.id,
      role: signInResp.role,
      seatType: signInResp.package,
    });
  };

  @action
  tryLoginFromLocalForage = (setIsLoggingIn = true) => {
    if (NODE_ENV_TEST) {
      return fromPromise(Promise.resolve());
    }
    return fromPromise(
      localforage
        .getItem<IPersonSignInTokenResponse>(SIGN_IN_RESPONSE)
        .then(async (localForageUser) => {
          if (!localForageUser) {
            console.debug(
              'localforage did not find the key ' + SIGN_IN_RESPONSE
            );
          }
          const hashToken = retrieveAndClearHashToken() as string | null;
          const tkn: string | null =
            hashToken || (localForageUser && localForageUser.token) || null;
          if (!isEmpty(tkn)) {
            await this.loginWithExistingToken(tkn, setIsLoggingIn);
          } else {
            this.setIsLoggingIn(false);
          }
          return tkn;
        })
    );
  };

  /** Select a `IPromiseBasedObservable` representing a `Person` */
  selectPersonById = createTransformer((personId: number) => {
    const pIdStr = isFinite(personId) ? personId.toString() : '';
    return this.personsById.has(pIdStr) ? this.personsById.get(pIdStr) : null;
  });

  getExtrContactByPhoneNumber = (phoneNumber: string) => {
    if (!phoneNumber) return null;

    const extrContactsPbo = this.getAllContactsWithSameNum(phoneNumber);
    if (extrContactsPbo?.state === 'fulfilled') {
      return extrContactsPbo.value[0];
    }
    return null;
  };

  getFromDirectory = async (phoneNumber: string): Promise<ContactModelV2> => {
    try {
      const res = await fromPromise(
        API.get<IContactApiPagination<IContactV2>>(
          API_ENDPOINTS.FetchContactsV2({
            sourcePlatforms: ['Bhive'],
            searchTerm: phoneNumber,
            orderBy: 'name',
            perPage: 1,
            page: 1,
          })
        )
      );
      return res?.data?.data?.length
        ? ContactModelV2.fromResponseDto(res.data.data[0])
        : null;
    } catch (err) {
      console.log('[error-searching-directory]: ', err);
    }
  };

  getContactFromList = (phoneNumber: string) => {
    return this.allContacts.find((item) => {
      return item?.phoneNumbers.find(
        (phone) =>
          isPhoneNumberMatch(phoneNumber, phone.number) ||
          isPhoneNumberMatch(phoneNumber, phone.normalizedNumber)
      );
    });
  };

  getAllContactsWithSameNum = (phoneNumber: string, forceLoad = false) => {
    try {
      const formattedPhoneNumber = formatNumberWithNationalCode(phoneNumber);

      if (
        !forceLoad &&
        this.listOfAllContactsWithNumber.has(formattedPhoneNumber)
      ) {
        return this.listOfAllContactsWithNumber.get(formattedPhoneNumber);
      }

      const resp = fromPromise(
        API.get<{ data: Record<string, IContactV2[]> }>(
          API_ENDPOINTS.MatchContactByPhoneNumber(),
          { params: { phoneNumbers: [phoneNumber] } }
        ).then((response) => {
          // Just getting the key of the returned contact, in case it comes in a different format
          const phoneNumberKey = Object.keys(response.data.data)[0];

          if (!phoneNumberKey) {
            return [];
          }

          return response.data.data[phoneNumberKey].map((contact) =>
            ContactModelV2.fromResponseDto(contact)
          );
        })
      );

      this.listOfAllContactsWithNumber.set(formattedPhoneNumber, resp);
      return resp;
    } catch (err) {
      bugsnagClient.notify(
        `Failed to get all contacts by phone: ${phoneNumber}`,
        (event) => {
          event.severity = 'error';
        }
      );
    }
  };

  selectPersonByExt = createTransformer((extension: number) => {
    return fromPromise<ContactModelV2>(
      new Promise((resolve, reject) => {
        const extStr = isFinite(extension) ? extension.toString() : '';
        if (extStr.length === 0) {
          reject(`Empty or invalid extension provided to selectPersonByExt`);
        }
        this.personsById.forEach((value) => {
          return value.case({
            fulfilled: (res) => {
              if (
                res.extensionNumbers &&
                res.extensionNumbers.length &&
                (res.extensionNumbers[0].number === extStr ||
                  res.extensionNumbers[0].number === 'x' + extStr)
              ) {
                resolve(res);
              }
            },
          });
        });
        // NO match
        reject(`selectPersonByExt found no match for ${extStr}`);
      })
    );
  });

  /** If the PBO for a `Person` is 'fulfilled', return the value, otherwise `null` */
  selectPersonValueById = createTransformer((personId: number) => {
    const personPbo = this.selectPersonById(personId);
    if (personPbo !== null && personPbo.state === 'fulfilled') {
      return personPbo.value;
    }
    return null;
  });

  selectPersonPropertiesById = createTransformer((personId: number) => {
    const pr = this.selectPersonValueById(personId);
    if (pr !== null) {
      return pr;
    }
    return null;
  });

  waitUntilLoggedIn = () => {
    return when(
      () => {
        return this.rootStore.personStore.IsLoggedIn;
      },
      { timeout: 10000 }
    );
  };

  /** Returns a `Promise` for removing `SIGN_IN_RESPONSE` from localforage */
  @action
  clearAllData = () => {
    if (this.wakeUpCheckTick !== null) {
      this.wakeUpCheckTick();
      this.wakeUpCheckTick = null;
    }
    this.personsById.clear();
    this.loginStatus = null;
    localforage.removeItem(STORE_CHAT_HTML);
    localforage.removeItem(STORE_CHAT_RAW);
    this.loggedInAccountId = -1;
    this.loggedInPersonId = -1;

    removeAxiosAuthHeaders();
    return localforage
      .removeItem(SIGN_IN_RESPONSE)
      .then(() => (this.loginTokenResponse = null));
  };

  @action
  handleVideoTrialInterested = () => {
    pushToGTMDataLayer('Video ProSeat, interested', {
      userId: this.loggedInPersonId,
    });
  };

  /** Load a single `Person` into `this.personsById` */
  @action
  loadPersonByIdGet = (
    personId: string
  ): IPromiseBasedObservable<ContactModelV2> => {
    try {
      const loadPersonPbo = fromPromise<ContactModelV2>(
        API.get<{ data: IContactV2 }>(API_ENDPOINTS.MatchContactBySource(), {
          params: {
            sourcePlatform: 'Bhive',
            sourceId: personId.toString(),
            includeDeleted: true,
          },
        })
          .then((response) => {
            const person = ContactModelV2.fromResponseDto(response.data.data);

            this.loadPersonByIdGetSuccess(person);

            return person;
          })
          .catch((reason) => {
            // SUP-6458: Call function that creates and returns fallback
            return this.loadPersonByIdGetFailure(reason, personId);
          })
      );

      this.personsById.set(personId, loadPersonPbo);

      return this.personsById.get(personId.toString());
    } catch (err) {
      console.warn(
        'Failed to load Person ' +
          personId.toString() +
          ' this may be caused by a removed Person, which is not an error.',
        err
      );
    }
  };

  /** Load a single `Person` into `this.personsById` only if the key (`personId`) is missing */
  @action
  loadPersonByIdGetIfMissingGet = (personId: string) => {
    const pidStr = personId?.toString();
    if (pidStr && !this.personsById.has(pidStr)) {
      return this.loadPersonByIdGet(personId);
    }
    return this.personsById.get(pidStr);
  };

  /** Convert plain object `IPersonModel` response DTO into `PersonModel` with observable properties and actions. */
  @action
  loadPersonByIdGetSuccess = (personResp: IContactV2) => {
    this.personsById.set(
      personResp.sourceId,
      fromPromise.resolve(ContactModelV2.fromResponseDto(personResp))
    );
  };

  /** Convert plain object `IPersonModel` response DTO into `PersonModel` with observable properties and actions. */
  @action
  removePersonById = (personId: string, softDelete = false) => {
    if (softDelete) {
      this.personsById.get(personId)?.case({
        fulfilled: (person) => {
          person.deleted = true;
        },
      });
    } else {
      this.personsById.delete(personId);
    }
  };

  /** Creates deleted users and formats them accordingly */
  @action
  private loadPersonByIdGetFailure = (
    reason: { response?: { status?: number } },
    personId: string
  ): ContactModelV2 | null => {
    const status = reason?.response?.status;
    const shouldCreateFallback = status && ![403].includes(status);

    if (shouldCreateFallback) {
      const personModel = new ContactModelV2();
      personModel.sourceId = personId;
      personModel.deleted = true;
      personModel.name = personId;
      personModel.firstName = personId;
      personModel.lastName = '';

      // Store in cache to avoid repeated requests
      this.personsById.set(
        personId.toString(),
        fromPromise.resolve(personModel)
      );

      // Return fallback model
      return personModel;
    }

    return null; // Only return null for 403
  };

  @action
  loadPeopleGet = () => {
    const request = API.get<{
      data: IContactV2[];
    }>(
      API_ENDPOINTS.FetchContactsV2({
        sourcePlatforms: ['Bhive'],
        searchTerm: '',
        orderBy: 'name',
        perPage: 10000,
        page: 1,
      })
    );

    return request.then(this.loadPeopleGetSuccess, (reason) => {
      /* CUU2-2822
      useNotificationStore.getState().addNotify({
        type: 'error',
        dismissible: true,
        title: 'Error loading contact’s info',
        message:
          'We couldn’t load your contact’s info. Please refresh the page.',
      }); */
    });
  };

  /**
   * Maps each person in `people` into `this.conversationByIdMap` as `AxiosResponseT<PersonModel>`.
   */
  @action
  private loadPeopleGetSuccess = (
    resp: AxiosResponseT<{
      data: IContactV2[];
    }>
  ) => {
    resp.data?.data?.forEach((contact) => {
      this.personsById.set(
        contact.sourceId,
        fromPromise.resolve(ContactModelV2.fromResponseDto(contact))
      );
    });
    return Promise.resolve(this.personsById);
  };

  /**
   * Creates an ephemeral Auth0 client for social account linking.
   * This client is created fresh, used once to get an ID token, then discarded.
   * The refresh token configuration exists for consistency but doesn't affect
   * behavior since no token refresh occurs on this client.
   *
   * The standalone client is necessary because we need to authenticate with
   * a secondary identity (e.g., Google, Microsoft) to link it to the primary
   * account. This requires a separate Auth0 session that doesn't interfere
   * with the main Auth0Provider session.
   */
  authenticateUser = async (): Promise<IdToken> => {
    const auth0 = await createAuth0Client(providerConfig);
    if (IS_ELECTRON && has(window, 'ipcRenderer')) {
      const auth0Url = await auth0.buildAuthorizeUrl({
        prompt: 'login',
        max_age: 0,
        scope: 'openid',
      });
      return new Promise((resolve, reject) => {
        handleCodeReceived(async (code, url) => {
          try {
            await auth0.handleRedirectCallback(decodeURIComponent(url));
            const res = await auth0.getIdTokenClaims();
            resolve(res);
          } catch (error) {
            reject(error);
          }
        });
        sendIpcLoginUrl(auth0Url);
      });
    } else {
      await auth0.loginWithPopup({
        max_age: 0,
      });
      return await auth0.getIdTokenClaims();
    }
  };

  prepareAxiosAuth0Headers = async (
    auth0: Auth0ContextInterface<Auth0User>
  ) => {
    const accessToken = await auth0.getAccessTokenSilently({
      scope: AUTH0_MANAGEMENT_API_SCOPE,
    });
    if (!accessToken) {
      throw new Error(
        `Couldn't complete your request, seems that you have been logged out!`
      );
    }
    AUTH0_API.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  };

  linkSocailAccount = async (auth0: Auth0ContextInterface<Auth0User>) => {
    await this.prepareAxiosAuth0Headers(auth0);
    const {
      __raw: targetUserIdToken,
      email_verified,
      email,
    } = await this.authenticateUser();

    if (!email_verified) {
      throw new Error(
        `Account linking is only allowed to a verified account. Please verify your email ${email}.`
      );
    }

    return AUTH0_API.post(`${auth0.user.sub}/identities`, {
      link_with: targetUserIdToken,
    });
  };

  unlinkSocialAccount = async (
    auth0: Auth0ContextInterface<Auth0User>,
    secondaryIdentity: Identity
  ) => {
    const { provider, user_id } = secondaryIdentity;
    await this.prepareAxiosAuth0Headers(auth0);
    return AUTH0_API.delete(
      `${auth0.user.sub}/identities/${provider}/${user_id}`
    );
  };

  getAuth0UserProfile = async (
    auth0: Auth0ContextInterface<Auth0User>,
    userId: string
  ) => {
    await this.prepareAxiosAuth0Headers(auth0);
    return AUTH0_API.get(`${userId}`);
  };

  whetherTheCallExists = () => {
    const {
      rootStore: {
        phoneStore: { ActivePhoneCall, phoneCalls },
      },
    } = this;
    return (
      isNullOrUndefined(ActivePhoneCall) ||
      isNullOrUndefined(phoneCalls) ||
      !ActivePhoneCall.isCallConnected ||
      !ActivePhoneCall.isCallConnecting ||
      !ActivePhoneCall.isCallOnHold ||
      !ActivePhoneCall.isCallMuted
    );
  };

  /** Attempt to use the data in `this.localForageUserLoaded` to call `loginWithExistingToken` */
  @action
  tryRefreshLocalLogin = () => {
    const {
      rootStore,
      rootStore: {
        personStore,
        pusherStore,
        uiStore,
        conversationStore,
        phoneStore,
      },
    } = this;
    if (NODE_ENV_TEST) {
      return;
    }
    if (this.whetherTheCallExists()) {
      clearTimeout(phoneStore.setTimeOut);
      phoneStore.setIsCallTimeout(false);
    }
    this.loginTokenResponse = this.tryLoginFromLocalForage(
      false
    ) as IPromiseBasedObservable<any>;
    this.loginTokenResponse.then((ltr) => {
      // No token retrieved from localforage or url hash
      if (ltr === null) {
        this.initiateAutoLogout(false);
      } else if (pusherStore.isOnline && navigator.onLine) {
        if (
          personStore.allContacts.length === 0 &&
          !personStore.loadingContacts
        ) {
          this.getAllContacts();
        }
        this.loginWithExistingToken(ltr.token, false);
        uiStore.loadAllPeoplesPresence();
        this.loadMessagesSinceNewestMessageForAllConversations();

        const ldFlags = this.ldClient.allFlags();

        // Not necessary if polling is enabled, because PollingProvider already takes care of reloading conversations
        if (!ldFlags?.[POLLING]?.polling) {
          conversationStore.loadConversationsGet();
        }

        personStore.getAllContacts();
        phoneStore.runRegister();
      } else {
        this.initiateAutoLogout(false);
      }
    });
  };

  /**
   * For simulating token expiration
   */
  @observable
  simulateTokenExpiration = false;

  /**
   * For simulating token expiration
   */
  @action
  setSimulateTokenExpiration = (simulateTokenExpiration: boolean) =>
    (this.simulateTokenExpiration = simulateTokenExpiration);

  /**
   * The time of the last token refresh in milliseconds
   */
  @observable
  timeOfLastTokenRefresh = 0;

  /**
   * Set the time of the last token refresh in milliseconds
   */
  @action
  setTimeOfLastTokenRefresh = (timeOfLastTokenRefresh: number) =>
    (this.timeOfLastTokenRefresh = timeOfLastTokenRefresh);

  /**
   * Check if the token is expired and refresh it if needed on both auth0 and bhive, and return the new bhive token.
   * @param forceRefresh - If true, force the token to be refreshed even if it's not expired
   * @returns The refreshed bhive token or undefined if the token could not be refreshed
   */
  getRefreshedToken = async (forceRefresh = false) => {
    const token = getAuthToken();

    if (!token) {
      return undefined;
    }

    try {
      const tokenStatus = isTokenAboutToExpire(token);

      let newToken: string | undefined;

      // If simulating token expiration, exchanges for a new token
      if (this.simulateTokenExpiration || forceRefresh) {
        this.setSimulateTokenExpiration(false);

        newToken = await this.exchangeForBVToken({ ignoreCache: true });
        // If token is expired, or is about to expire and it's been more than 1 minute since last refresh, exchanges for a new token
      } else if (
        tokenStatus === 'expired' ||
        tokenStatus === 'invalid' ||
        (tokenStatus === 'about-to-expire' &&
          Date.now() - this.timeOfLastTokenRefresh >= ATE_REFRESH_BUFFER_MS)
      ) {
        newToken = await this.exchangeForBVToken({ ignoreCache: true });
      }

      // If a new token was exchanged successfully, set the time of the last token refresh
      if (newToken) {
        this.setTimeOfLastTokenRefresh(Date.now());
        return newToken;
      }

      return undefined;
    } catch {
      return undefined;
    }
  };

  /**
   * Exchange the auth0 token for a bhive token.
   * Retry up to 3 times with exponential backoff if the exchange fails.
   * If the exchange fails due to an offline error, schedule a retry.
   * If the exchange fails due to an auth0 error, log out the user.
   * @param options - The options for getAccessTokenSilently()
   * @returns The bhive token or undefined if the token could not be exchanged
   */
  exchangeForBVToken = async (
    options: ExchangeForBVTokenOptions = {}
  ): Promise<string | undefined> => {
    const { suppressLogoutOnAuthFailure, ...tokenOptions } = options;

    try {
      return await retryWithBackoff({
        operation: async () => {
          const accToken = await this.auth0.getAccessTokenSilently({
            ...tokenOptions,
            ignoreCache: this.simulateTokenExpiration
              ? true
              : tokenOptions.ignoreCache,
          });
          return await this.auth0ToBHiveToken(accToken);
        },
        initialDelay: 1000,
        maxDelay: 5000,
        maxAttempts: 3,
        backoffFactor: 2,
        jitter: true,
        timeout: 15000,
        /**
         * If the error is not auth0 related and the browser is online, retry.
         * If the browser is offline, there's no need to retry, we'll wait for the online event.
         */
        shouldRetry: (error) =>
          !isAuthRequiredError(error) && !isOfflineError(error),
        onRetry: (error, attempt, nextDelay) => {
          bugsnagClient.leaveBreadcrumb(
            '[Token Exchange] Token exchange retry scheduled',
            {
              attempt: attempt + 1,
              nextDelayMs: nextDelay,
              classification: getTokenExchangeClassification(error),
            }
          );
        },
      });
    } catch (e) {
      bugsnagClient.leaveBreadcrumb('[Token Exchange] Token exchange failed', {
        classification: getTokenExchangeClassification(e),
      });

      if (isOfflineError(e)) {
        this.tokenExchangeScheduleService.schedule();
        return;
      }

      if (!suppressLogoutOnAuthFailure) {
        bugsnagClient.leaveBreadcrumb(
          '[Token Exchange] Logging out due to token exchange failure'
        );
        await this.initiateAutoLogout(true);
      }
      throw e;
    }
  };

  @observable
  refreshingToken = false;

  @action
  setRefreshingToken = (refreshing: boolean) =>
    (this.refreshingToken = refreshing);

  @action
  refreshTokenIfNeededForPusher = async (): Promise<boolean> => {
    if (this.refreshingToken) {
      return true;
    }

    try {
      this.setRefreshingToken(true);

      const newToken = await this.getRefreshedToken();

      if (newToken) {
        await this.loginWithExistingToken(newToken, false);
        return true;
      }

      return getAuthToken() !== null;
    } catch (error) {
      bugsnagClient.notify(error as Error, (event) => {
        event.severity = 'error';
        event.context = 'PersonStore.refreshTokenIfNeededForPusher';
      });

      return false;
    } finally {
      this.setRefreshingToken(false);
    }
  };

  /** Attempt to use the data in `this.localForageUserLoaded` to call `loginWithExistingToken` */

  @action
  loadMessagesSinceNewestMessageForAllConversations = () => {
    const {
      rootStore: { messageStore, preferenceStore },
    } = this;
    messageStore.groupedMessagesByConversationMap.forEach((element) => {
      if (element.NewestMessage !== null) {
        messageStore.loadMessagesSinceNewestMessage(
          element.conversationId,
          element.NewestMessage.id
        );
      } else {
        const msgsGet: MessagesGetRequest = {
          Limit: 31,
          ShowCallMessagesInChat:
            preferenceStore.preferences.showCallMessagesInChat,
        };
        messageStore.loadConversationMessages(element.conversationId, msgsGet);
      }
    });
  };

  @action
  loginWithExistingToken = (token: string, setIsLoggingIn = true) => {
    if (setIsLoggingIn) {
      this.setIsLoggingIn(true);
    }

    this.setFirstLogin(false);
    if (!isNullOrUndefined(localforage.getItem(STORE_CHAT_HTML))) {
      localforage
        .getItem<ObservableMap<string>>(STORE_CHAT_HTML)
        .then((data) => {
          this.rootStore.messageStore.messageDraftHtmlMap.merge(data);
        });
      localforage
        .getItem<ObservableMap<string>>(STORE_CHAT_RAW)
        .then((data) => {
          this.rootStore.messageStore.messageDraftRawMap.merge(data);
        });
    }
    if (!isNullOrUndefined(token)) {
      const link: HTMLLinkElement =
        document.querySelector("link[rel*='icon']") ||
        document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      this.loginStatus = fromPromise(
        this.getRefreshedToken().then((refreshedToken) => {
          const authToken = refreshedToken ?? token;

          return PURE_API.get(API_ENDPOINTS.AuthLogin, {
            headers: { Authorization: `Bearer ${authToken}` },
          }).then(
            (resp) =>
              ({
                ...resp,
                data: {
                  ...resp.data,
                  token: authToken,
                },
              } as AxiosResponseT<IPersonSignInTokenResponse>)
          );
        })
      );
      // Removed toast notification, use inline Message if login failed
      return this.loginStatus.then(
        (resp) => {
          const data: IPersonSignInTokenResponse = {
            ...resp.data,
          };
          /** if setIsLoggingIN is false means we already have a token and we wont reconfig */
          this.loginSuccess(
            { ...resp, data } as AxiosResponseT<IPersonSignInTokenResponse>,
            false,
            false,
            setIsLoggingIn
          );
        },
        (err) => {
          this.setIsLoggingIn(false);
          if (err && err.response && err.response.status === 401) {
            link.href = autoLogOut;
            document.getElementsByTagName('head')[0].appendChild(link);
            void this.initiateAutoLogout(true);
            console.error(err);
          } else {
            throw err;
          }
        }
      );
    } else {
      console.warn('the token is empty');
    }
  };

  /**
   * Fetches the person model for the logged in person.
   * @returns A promise that resolves to the person model for the logged in person.
   * @throws An error if the person model cannot be fetched.
   */
  @action
  getUserProfile = async (): Promise<PersonModel | undefined> => {
    try {
      const resp: AxiosResponseT<IPersonSignInTokenResponse> = await API.get(
        API_ENDPOINTS.AuthLogin
      );
      const signedInPerson: PersonModel = PersonModel.FromResponseDto(
        resp.data
      );

      return signedInPerson;
    } catch (err) {
      /* CUU2-2822
      useNotificationStore.getState().addNotify({
        type: 'error',
        dismissible: true,
        title: 'Error loading profile settings',
        message:
          'We couldn’t load the profile settings. Please refresh the page',
      }); */

      this.rootStore.notificationStore.addNotification(
        'Failed to get user profile',
        null,
        'error'
      );
      return undefined;
    }
  };

  /**
   * Fetches the contact model for the logged in person and updates the store.
   * @returns A promise that resolves to the contact model for the logged in person.
   */
  @action
  getUserProfileV2 = async (): Promise<ContactModelV2> => {
    return this.loadPersonByIdGet(this.loggedInPersonId.toString());
  };

  /**
   * Exchanges an Auth0 access token for a BHive access token.
   * @param accessToken - The Auth0 access token to exchange.
   * @returns A promise that resolves to the BHive access token.
   * @throws An error if the access token is null or undefined.
   * @throws An error if the token exchange fails.
   */
  @action
  auth0ToBHiveToken = async (
    accessToken: string
  ): Promise<string | undefined> => {
    if (isNullOrUndefined(accessToken)) {
      throw new Error('Access token should not be null or undefined!');
    }

    const { data } = await PURE_API.post<{ accessToken: string }>(
      '/v3/authentication/auth0_to_bhive',
      { accessToken },
      {
        baseURL: PORTAL_URL,
      }
    );

    return data?.accessToken;
  };

  @action
  login = (email: string, password: string) => {
    this.setIsLoggingIn(true);
    this.setFirstLogin(true);
    this.loginStatus = fromPromise(
      API.post(API_ENDPOINTS.AuthLogin, {
        Email: email,
        Password: password,
        Service: 'communicator',
      })
    );
    // Removed toast notification, use inline Message if login failed
    return this.loginStatus.then((resp) => {
      if (IS_ELECTRON && has(window, 'ipcRenderer')) {
        sendIpcSaveCredentials(email, password);
      }
      return this.loginSuccess(resp);
    });
  };

  /**
   * This method performs all necessary operations for logging out a user.
   * It resets the app badge count, shuts down Intercom, updates the user's presence to 'OffLine',
   * pushes 'logout' event to GTM data layer, sets 'isLoggingOut' to true, clears all data from the root store,
   * and removes the SIGN_IN_RESPONSE item from localforage.
   *
   * @throws {Error} If any of the operations fail, an error is thrown.
   *
   * @returns {Promise<void>} A promise that resolves when all operations have completed.
   */
  @action
  processLogoutOperations = async () => {
    // Reset the app badge count. This does not actually affect the unread counts in the core app
    try {
      sendIpcUnreadCounts({
        conversationUnreadCounts: { unreadMentions: 0, unreadMessages: 0 },
        totalUnreadCounts: { unreadMentions: 0, unreadMessages: 0 },
      });
      intercom.shutdown();
      await this.rootStore.uiStore.updatePresence('OffLine', false, '');
      pushToGTMDataLayer('logout', {
        logoutType: 'Explicit',
      });
      this.setIsLoggingOut(true);
      await this.rootStore.clearAllData();
      await localforage.removeItem(SIGN_IN_RESPONSE);
    } catch (error) {
      bugsnagClient.notify('Error during logout:', (event) => {
        event.severity = 'error';
        event.context = 'processLogoutOperations';
        event.addMetadata('custom', { function: 'processLogoutOperations' });
      });
    }
  };

  /**
   * Displays a logout notification, waits 4 seconds, then performs logout.
   *
   * Shows a system notification to inform the user of automatic logout,
   * delays for visibility, then either performs a full Auth0 logout or
   * clears local data and redirects based on the provided options.
   *
   * @param fullLogout - If true, terminates Auth0 session; else only clears local data.
   * @param isAutoLogout - If true, sets the `isAutoLogout` flag in the store.
   */
  @action
  initiateAutoLogout = async (
    fullLogout: boolean,
    isAutoLogout?: boolean
  ): Promise<void> => {
    if (this.isLoggingOut) {
      return;
    }

    this.setIsLoggingOut(true);

    try {
      await this.showAutoLogoutNotification();
      if (fullLogout) {
        await this.logout();
      } else {
        if (isAutoLogout) {
          this.setIsAutoLogout(true);
        }
        await this.rootStore.clearAllData();
        this.rootStore.routerStore.replace('/');
      }
    } catch (error) {
      this.setIsLoggingOut(false);
      console.error('Error during initiateAutoLogout:', error);
    }
  };

  showAutoLogoutNotification = async () => {
    this.rootStore.notificationStore.addWebNotification(
      "You've been logged out",
      {
        tag: `ec:auto-logout:${Date.now()}`,
        body: "Your Communicator session has expired and you've been logged out. Log back in to receive calls and messages.",
      },
      'info',
      true
    );
    await new Promise((resolve) =>
      setTimeout(resolve, AUTO_LOGOUT_NOTIFICATION_DELAY)
    );
  };

  @action
  logout = async () => {
    try {
      await this.processLogoutOperations();
      this.auth0.logout({
        returnTo: window.location.origin,
      });
      terminateClaritySession();
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  @action
  resetContactList = () => (this.allContacts = observable.array());

  @action
  loadTopBarContactsOnSuccess = async (
    resp: AxiosResponseT<IContactApiPagination<IContactV2>>,
    appendResult = false
  ) => {
    let data = resp.data.data;
    if (appendResult) {
      data = [...this.allContactsTopBar, ...data];
    }

    const newContactListResp = data.map(async (contact) => {
      if (contact.hasAvatar && !contact.avatarUrl) {
        return this.loadContactProfilePic(contact);
      } else {
        return ContactModelV2.fromResponseDto(contact);
      }
    });

    const newContactList = await Promise.all(newContactListResp);
    runInAction(() => (this.allContactsTopBar = newContactList));
    return Promise.resolve(newContactList);
  };

  @action
  getSearchContactsTopBar = async (
    limit = 20,
    page: number = this.contactPageNumber,
    query = '',
    appendResult?: boolean
  ) => {
    try {
      const resp = await API.get<IContactApiPagination<IContactV2>>(
        API_ENDPOINTS.FetchContactsV2({
          sourcePlatforms: ['ContactAPI', 'Nylas'],
          perPage: limit,
          page,
          searchTerm: query,
        })
      );
      if (!appendResult && resp.data?.data.length === 0) {
        runInAction(() => (this.allContactsTopBar = observable.array()));
      }
      return resp.data.data?.length > 0
        ? this.loadTopBarContactsOnSuccess(resp, appendResult)
        : [];
    } catch (e) {
      /* CUU2-2822
      useNotificationStore.getState().addNotify({
        type: 'error',
        dismissible: true,
        title: 'Error searching contacts',
        message:
          'It wasn’t possible to search contacts. Please refresh the page.',
      }); */

      return [];
    }
  };

  @action
  getSearchListContacts = async (
    limit = 20,
    page: number = this.contactPageNumber,
    query = '',
    appendResult?: boolean,
    source?: string
  ) => {
    let sourcePlatforms: TSourcePlatform[];
    let sourceAccountId: string | undefined;
    let sourceType: string | undefined;

    if (source) {
      if (['personal', 'shared'].includes(source)) {
        sourcePlatforms = ['ContactAPI'];
        sourceType = source;
      } else {
        sourcePlatforms = ['Nylas'];
        sourceAccountId = source;
      }
    } else {
      sourcePlatforms = ['ContactAPI', 'Nylas'];
    }

    if (!this.loadingContacts) {
      // prevent loader from showing when scrolling on infinite scroll component
      !appendResult
        ? this.setLoadingContact(true)
        : this.setLoaderInfinite(true);
      try {
        const resp = await API.get<IContactApiPagination<IContactV2>>(
          API_ENDPOINTS.FetchContactsV2({
            sourcePlatforms,
            perPage: limit,
            page,
            searchTerm: query,
            sourceType,
            sourceAccountId,
          })
        );

        if (resp.data?.data.length === 0) {
          this.setLoadingContact(false);
          this.setLoaderInfinite(false);
          //reset in case source is picked, query is not null & page is 1
          page === 1 && (source || query) && this.resetContactList();
        }
        return resp.data.data?.length > 0
          ? this.loadContactsOnSuccess(resp, appendResult)
          : [];
      } catch (e) {
        /* CUU2-2822
        useNotificationStore.getState().addNotify({
          type: 'error',
          dismissible: true,
          title: 'Error searching for contacts',
          message: 'It wasn’t possible to search contacts. Please try again.',
        }); */

        this.setLoadingContact(false);
        this.setLoaderInfinite(false);
        return [];
      }
    }
  };

  changeProviderName = (source) => {
    const lowerCaseSource = source.toLowerCase();

    if (lowerCaseSource.includes('bhive')) {
      return 'B-Hive';
    }
    switch (lowerCaseSource) {
      case 'icloud':
        return 'iCloud';
      case 'outlook':
      case 'office365':
      case 'eas':
        return 'Outlook';
      case 'gmail':
        return 'Gmail';
      case 'google':
        return 'Google';
      case 'microsoft':
        return 'Microsoft';
    }
    return '';
  };

  getSourceById = (id: string) =>
    this.allSources.find((source) => source.id.toString() === id);

  @action
  getAllContacts = async () => {
    this.setLoadingContact(true);
    try {
      const resp = await API.get<IContactApiPagination<IContactV2>>(
        API_ENDPOINTS.FetchContactsV2({
          sourcePlatforms: ['ContactAPI', 'Nylas'],
          perPage: 20,
        })
      );
      void this.loadTopBarContactsOnSuccess(resp, false);
      return this.loadContactsOnSuccess(resp, false);
    } catch (e) {
      this.setLoadingContact(false);
    }
  };

  @action
  loadContactsOnSuccess = async (
    resp: AxiosResponseT<IContactApiPagination<IContactV2>>,
    appendResult = false
  ) => {
    let data = resp.data.data;
    this.setHasContact(true);
    this.setHasMoreContacts(!!resp.data.links.next);

    if (appendResult) {
      data = [...this.allContacts, ...data];
    }

    const newContactListResp = data.map(async (contact) => {
      if (contact.hasAvatar && !contact.avatarUrl) {
        return this.loadContactProfilePic(contact);
      } else {
        return ContactModelV2.fromResponseDto(contact);
      }
    });

    const newContactList = await Promise.all(newContactListResp);
    runInAction(() => (this.allContacts = newContactList));

    this.setLoadingContact(false);
    this.setLoaderInfinite(false);

    return Promise.resolve(newContactList);
  };

  loadContactProfilePic = async (contact: IContactV2) => {
    const newContact = ContactModelV2.fromResponseDto(contact);
    try {
      const image = await this.loadContactsPicture(newContact.id);
      newContact.avatarUrl = image;
      return newContact;
    } catch (e) {
      /* CUU2-2822
      useNotificationStore.getState().addNotify({
        type: 'error',
        dismissible: true,
        title: 'Error loading contact profile picture',
        message:
          'We couldn’t load the contact profile picture. Please try again.',
      }); */

      return newContact;
    }
  };

  @action
  loadContactsPicture = async (contactId: string) => {
    try {
      const response = await API.get(API_ENDPOINTS.FetchAvatar(contactId), {
        responseType: 'arraybuffer',
      });
      const base64 = btoa(
        new Uint8Array(response.data).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ''
        )
      );
      return 'data:;base64,' + base64;
    } catch (err) {
      bugsnagClient.notify(
        'Failed to load contact profile picture',
        (event) => {
          event.severity = 'error';
        }
      );
    }
  };

  createBody = async (code: string, provider: string) => {
    const microsoftOauthProviders = ['microsoft', 'office365'];

    const redirectUrl = microsoftOauthProviders.includes(provider.toLowerCase())
      ? `${REDIRECT_URL}/addressBook/sources`
      : REDIRECT_URL;

    return {
      source: 'Nylas',
      sourceData: {
        type: provider,
      },
      redirectUrl,
      code,
    };
  };

  updateSourceListIfNeeded = (provider: string, data: SourceModelV2) => {
    this.setAddedNewSource({
      show: true,
      sourceProvider: provider,
      id: data?.id,
    });

    const newItem = {
      ...data,
      providerImage: this.mapProperLogoProvide(data.sourceData.type),
      providerImageSmall: this.mapProperLogoProvide(data.sourceData.type, true),
    };

    const isInList = this.allSources.find((item) => item.id === newItem.id);

    const newSourceList = isInList
      ? this.allSources.map((item) => (item.id === newItem.id ? newItem : item))
      : [...this.allSources, newItem];

    runInAction(() => (this.allSources = newSourceList));
  };

  @action
  sendCodeOfProvider = async (code: string, provider: string) => {
    const body = await this.createBody(code, provider);
    try {
      const source = await API.post<{ data: ISource }>(
        `${BASE_CONTACT_API_V2}sources`,
        body
      ).then((response) => SourceModelV2.fromResponseDto(response.data.data));

      if (source) this.updateSourceListIfNeeded(provider, source);

      return source;
    } catch (e) {
      if (e?.response.status === 401) {
        useNotificationStore.getState().addNotify({
          type: 'error',
          dismissible: true,
          title: 'Error connecting the source',
          message:
            'The credentials you entered are incorrect. Please check and try again.',
        });
      } else {
        useNotificationStore.getState().addNotify({
          type: 'error',
          dismissible: true,
          title: 'Error connecting the source',
          message: 'We couldn’t connect to the source. Please try again.',
        });
      }
      return false;
    }
  };

  @action
  removeSource = async (source: SourceModelV2) => {
    try {
      await API.delete(`${BASE_CONTACT_API_V2}sources/${source.id}`);

      const filteredSources = this.allSources.filter(
        (item) => item.id !== source.id
      );

      const filteredList = this.allContacts.filter(
        (item) =>
          item.sourceData.accountId !== source.id.toString() &&
          item.sourceData.type !== source.sourceData.type
      );

      runInAction(() => {
        this.allSources = filteredSources;
        this.allContacts = filteredList;
      });
      return filteredSources;
    } catch (e) {
      useNotificationStore.getState().addNotify({
        type: 'error',
        dismissible: true,
        title: 'Error deleting source',
        message: 'The source wasn’t deleted. Please try again.',
      });

      return false;
    }
  };

  @action
  resyncSource = async (source: SourceModelV2) => {
    try {
      const res = await API.post<{ data: ISource }>(
        `${BASE_CONTACT_API_V2}sources/${source.id}/sync`
      );

      const updatedSource = SourceModelV2.fromResponseDto(res.data.data);

      updatedSource.providerImage = this.mapProperLogoProvide(
        updatedSource.sourceData.type
      );

      updatedSource.providerImageSmall = this.mapProperLogoProvide(
        updatedSource.sourceData.type,
        true
      );

      runInAction(() => {
        this.allSources = this.allSources.map((source) =>
          source.id === updatedSource.id ? updatedSource : source
        );
      });
    } catch (e) {
      useNotificationStore.getState().addNotify({
        type: 'error',
        dismissible: true,
        title: 'Error resyncing source',
        message: 'You need to wait a moment before syncing your account again.',
      });
      return false;
    }
  };

  getSourcesIfMissing = async () => {
    if (this.allSources.length === 0) {
      return this.getSourceAccounts();
    } else {
      return this.allSources;
    }
  };

  mapProperLogoProvide = (provider, small = false) => {
    let providerImage = '';
    switch (provider) {
      case 'google-oauth2':
      case 'gmail':
      case 'google':
        providerImage = small ? googleIconShort : googleIcon;
        break;
      case 'windowslive':
      case 'outlook':
      case 'office365':
      case 'eas':
      case 'ews':
        providerImage = small ? outLookIconShort : outLookIcon;
        break;
      case 'linkedin':
        providerImage = linkedinIcon;
        break;
      case 'microsoft':
        providerImage = small ? microsoftIconShort : microsoftIcon;
        break;
      case 'salesforce':
      case 'salesforce-sandbox':
      case 'salesforce-community':
        providerImage = salesforceIcon;
        break;
      case 'apple':
      case 'icloud':
        providerImage = small ? iCloudIconShort : iCloudIcon;
        break;
      case 'bhive':
        providerImage = small ? bhiveIconShort : bhiveIcon;
    }
    return providerImage;
  };

  //Contacts section
  @action addNewContact = async (contact: IFormContact) => {
    try {
      const avatarUrl =
        this.contactProfilePictureLocally &&
        (await this.uploadContactProfileToS3());

      contact.avatarUrl = avatarUrl || '';
      contact.source = 'ContactAPI';

      this.setAddingNewContact(true);
      const resp = await API.post<{ data: IContactV2 }>(
        API_ENDPOINTS.CreateContact(),
        contact
      ).then((response) => ContactModelV2.fromResponseDto(response.data.data));

      await this.handleUpdateContactSuccess(resp);
      await this.handleAddNewContactSuccess();
      this.setAddingNewContact(false);

      return resp;
    } catch (e) {
      if (e.request.response.includes('phoneNumber')) {
        this.setErrorNewContact({
          show: true,
          message: 'Phone number is not in a correct format.',
          field: 'phoneField1',
        });
        return;
      } else if (e.request.response.includes('email')) {
        this.setErrorNewContact({
          show: true,
          message: 'Email is not in a correct format.',
          field: 'emailField1',
        });
        return;
      }

      useNotificationStore.getState().addNotify({
        type: 'error',
        dismissible: true,
        title: 'Error adding new contact',
        message: 'The contact wasn’t added. Please try again.',
      });
    }
  };

  deleteContactFromPhonesList = (phoneNum: string, formatedPhone: string) => {
    this.listOfAllContactsWithNumber.delete(phoneNum) ||
      this.listOfAllContactsWithNumber.delete(formatedPhone);
  };

  deleteContactFromListOfAllContactsWithNumber = (
    contactToDelete: ContactModelV2
  ) => {
    const listOfUpdates = new Map<string, ContactModelV2[]>();

    this.listOfAllContactsWithNumber.forEach((contacts, phoneNumber) => {
      if (contacts.state !== 'fulfilled') {
        return;
      }

      const contactFound = contacts.value.some(
        (contact) => contact.id === contactToDelete?.id
      );

      if (contactFound) {
        listOfUpdates.set(
          phoneNumber,
          contacts.value.filter((contact) => contact.id !== contactToDelete?.id)
        );
      }
    });

    if (listOfUpdates.size > 0) {
      runInAction(() => {
        listOfUpdates.forEach((updatedContacts, phoneKey) => {
          this.listOfAllContactsWithNumber.set(
            phoneKey,
            fromPromise(Promise.resolve(updatedContacts))
          );
        });
      });
    }
  };

  @action updateContact = async (contact: IFormContact) => {
    try {
      const avatarUrl =
        !contact.sourceData.accountId &&
        this.contactProfilePictureLocally &&
        (await this.uploadContactProfileToS3());

      if (!contact.sourceData.accountId && (avatarUrl || contact.avatarUrl)) {
        contact.avatarUrl = avatarUrl || contact.avatarUrl;
      }

      contact.source = 'ContactAPI';

      const { ...contactForApi } = contact;

      const id = contact.id;

      const cleanedFormData = {
        ...omitBy(contactForApi, (value, key) => {
          return key === 'pictureUrl';
        }),
      };

      const response = await API.put<{ data: IContactV2 }>(
        API_ENDPOINTS.UpdateContact(id),
        cleanedFormData
      );

      if (response) {
        return await this.handleUpdateContactSuccess(response.data.data);
      }
    } catch (e) {
      if (e.request.response.includes('phoneNumber')) {
        this.setErrorNewContact({
          show: true,
          message: 'Phone number is not in a correct format.',
          field: 'phoneField1',
        });
        return;
      } else if (e.request.response.includes('email')) {
        this.setErrorNewContact({
          show: true,
          message: 'Email is not in a correct format.',
          field: 'emailField1',
        });
        return;
      }

      useNotificationStore.getState().addNotify({
        type: 'error',
        dismissible: true,
        title: 'Error editing the contact',
        message: 'The contact wasn’t edited. Please try again.',
      });
    }
  };

  @action
  removeContact = async (contactId: string) => {
    try {
      const resp = await API.delete(API_ENDPOINTS.DeleteContact(contactId));
      if (resp) {
        this.handleRemoveSuccess(contactId);
      }
    } catch (e) {
      useNotificationStore.getState().addNotify({
        type: 'error',
        dismissible: true,
        title: 'Error deleting the contact',
        message: 'The contact wasn’t deleted. Please try again.',
      });
    }
  };

  @action
  handleRemoveSuccess = (contactId: string) => {
    const filteredList = this.allContacts.filter(
      (item) => item.id !== contactId
    );
    this.allContacts = filteredList as any;
  };

  /**
   * Checks if the contact is saved in any phone number key in listOfAllContactsWithNumber, which is not part of that contact.
   * If so, returns a map of the phone number keys and the contacts that should remain in the map, for that phone number key.
   */
  private preparePhoneNumberUpdates(updatedContact: ContactModelV2) {
    const phoneNumberUpdates = new Map<string, ContactModelV2[]>();

    this.listOfAllContactsWithNumber.forEach((contactsPbo, phoneKey) => {
      if (!phoneKey || contactsPbo?.state !== 'fulfilled') {
        return;
      }

      const contacts = contactsPbo.value;

      // If this phone key has no contacts, there's nothing to update
      if (!contacts) {
        return;
      }

      const matchingContact = contacts.find(
        (existingContact) => existingContact.id === updatedContact.id
      );

      // If the contact is not found in the list of contacts for this phone key, there's nothing to update
      if (!matchingContact) {
        return;
      }

      const hasMatchingNumber = updatedContact.phoneNumbers.some(
        (phoneNumber) =>
          phoneNumber?.number === phoneKey ||
          formatNumberWithNationalCode(phoneNumber?.number) === phoneKey
      );

      // If the contact doen't have this phone number, then we have to remove it from the list of contacts for this phone key
      if (!hasMatchingNumber) {
        phoneNumberUpdates.set(
          phoneKey,
          contacts.filter(
            (existingContact) => existingContact.id !== updatedContact.id
          )
        );
      }
    });

    return phoneNumberUpdates;
  }

  @action
  handleUpdateContactSuccess = async (contact: IContactV2) => {
    let newContact = ContactModelV2.fromResponseDto(contact);

    if (newContact.hasAvatar) {
      newContact = await this.loadContactProfilePic(newContact);
    }

    const phoneNumberUpdates = this.preparePhoneNumberUpdates(newContact);

    runInAction(() => {
      // Update listOfAllContactsWithNumber
      phoneNumberUpdates.forEach((updatedContacts, phoneKey) => {
        this.listOfAllContactsWithNumber.set(
          phoneKey,
          fromPromise(Promise.resolve(updatedContacts))
        );
      });

      // Update allContacts
      const contactIndexToRemove = this.allContacts.findIndex(
        (item) => item.id === contact.id
      );
      const updatedContacts = observable.array([...this.allContacts]);
      updatedContacts.splice(contactIndexToRemove, 1, newContact);
      this.allContacts = updatedContacts;
    });

    newContact.phoneNumbers.forEach((phoneNumber) => {
      void this.getAllContactsWithSameNum(phoneNumber.number, true);
    });

    return newContact;
  };

  @action
  handleAddNewContactSuccess = async () => {
    this.setContactPageNumber(1);

    await this.getSearchListContacts(
      CONTACTS_PER_PAGE,
      1,
      this.rootStore.personStore.contactSearchTerm,
      undefined,
      this.rootStore.personStore.contactFilterValue?.value?.toString()
    );
  };

  @observable
  contactProfilePictureLocally: File = null;

  @action
  setContactPictureLocally = (file: File) =>
    (this.contactProfilePictureLocally = file);

  uploadContactProfileToS3 = async () => {
    const file = this.contactProfilePictureLocally;
    try {
      const imageData = await API.post<{ data: IAvatarUploadResponse }>(
        API_ENDPOINTS.AvatarUpload(),
        {
          contentType: file.type,
        }
      );
      if (imageData?.data?.data) {
        const formData = this.createFormDataWithFile(imageData.data.data);
        await fromPromise(
          PURE_API.post(`${imageData.data.data.url}`, formData, {
            headers: {
              'Content-Type': file.type,
              'Content-Disposition': `attachment; filename=${file.name}`,
            },
          })
        );
        this.contactProfilePictureLocally = null;
        return imageData.data.data.key;
      }
      // case if BE doesnt return proper data
      throw null;
    } catch (err) {
      bugsnagClient.notify(
        'Failed to upload contact profile picture to S3',
        (event) => {
          event.severity = 'error';
        }
      );
      return null;
    }
  };

  createFormDataWithFile = ({ fields }: IAvatarUploadResponse) => {
    const formData = new FormData();
    const file = this.contactProfilePictureLocally;
    Object.keys(fields).forEach((key) => formData.append(key, fields[key]));
    formData.append('file', file);
    return formData;
  };

  @action
  getSourceAccounts = async () => {
    const resp = await API.get<IContactApiPagination<ISource>>(
      `${BASE_CONTACT_API_V2}sources`
    );

    if (!resp.data.data) {
      return [];
    }

    const sourceList = resp.data.data.map((item) => {
      const source = SourceModelV2.fromResponseDto(item);

      source.providerImage = this.mapProperLogoProvide(item.sourceData.type);
      source.providerImageSmall = this.mapProperLogoProvide(
        item.sourceData.type,
        true
      );

      return source;
    });

    runInAction(() => {
      this.allSources = sourceList;
    });
    return sourceList;
  };

  checkKeyInScopes = (scopes: string[], key: string) =>
    scopes.some((el) => el.includes(key));

  checkAvailabilityPhoneNumber = (inboundNumbers: InboundNumber[]) => {
    /* FIXME:
     * This is a steping stone while we still have to use
     * this MobX store
     * Ideally in the future this will be managed
     * directly through hooks.
     */
    void this.#queryClient.fetchQuery({
      queryKey: [QUERY_KEY_CHECK_AVAILABILITY_PHONENUMBER, ...inboundNumbers],
      queryFn: async () => fetchAvailabilityPhoneNumberQuery(inboundNumbers),
    });
  };

  @action
  private setUserScopes = (
    scopes: string[],
    signedInPerson: IPersonSignInTokenResponse
  ) => {
    const video = { enabled: false, expirationDate: '' };
    if (this.checkKeyInScopes(scopes, 'video')) {
      const timeLeft = this.rootStore.uiStore.getTimeLeft(
        signedInPerson.videoTrialEndsAt
      );
      video.enabled = timeLeft !== 'expired';
      video.expirationDate = signedInPerson.videoTrialEndsAt;
    }
    this.personAvaliableFeatures = {
      video,
      socialAccounts: this.checkKeyInScopes(scopes, 'social_linking'),
    };
  };

  @action
  private loginSuccess = async (
    resp: AxiosResponseT<IPersonSignInTokenResponse>,
    redirectOnLoaded = true,
    forceReloadConversations = false,
    shouldLoadConfig = true
  ) => {
    this.loginStatus =
      fromPromise.resolve<AxiosResponseT<IPersonSignInTokenResponse>>(resp);
    const signedInPerson = resp.data;
    if (window && !isEmpty(resp.data)) {
      window['_currentPerson'] = {
        id: resp.data.id,
        email: resp.data.email,
        account_id: resp.data.account.id,
        location_id: resp.data.location.id,
      };
    }

    const jwt = parseJwt(resp.data.token);
    signedInPerson.role = jwt.acl;
    this.loggedInPersonRole = jwt.acl;

    this.setLoggedInInfo(signedInPerson);

    this.setUserScopes(jwt.scopes, signedInPerson);

    this.rootStore.personStore.setIsAutoLogout(false);

    // this is very important to keep (for now) as it is used for recovering the session after connection loss
    // in the future we can rely on the zustand store with persistence, so we can keep the token across browser sessions
    localforage.setItem(SIGN_IN_RESPONSE, signedInPerson);

    // Since we have this data already fetched, lets save it in the upcoming zustand Person store
    usePersonStore.setState({ ...signedInPerson });
    this.checkAvailabilityPhoneNumber(signedInPerson.inboundNumbers);

    await this.rootStore.contactStore.loadContacts();

    pushToGTMDataLayer('login', {
      accountId: signedInPerson.account.id,
      locationId: signedInPerson.location.id,
      // personId: signedInPerson.id, // Pretty sure we aren't allowed to collect personalized information on this level by the GA EULA (RP 2020-01-30)
      package: signedInPerson.package,
    });

    sendIpcIdentify(
      get(signedInPerson, 'account.id', 0).toString(),
      get(signedInPerson, 'id', 0).toString(),
      `${signedInPerson.firstName} ${signedInPerson.lastName}`,
      signedInPerson.email,
      signedInPerson.package,
      get(signedInPerson, 'locationId', 0).toString()
    );
    await this.loadPeopleGet();

    void this.rootStore.uiStore.getMutedConv();

    void this.#queryClient.fetchQuery({
      queryKey: [QUERY_KEY_FETCH_CONVERSATIONS],
      queryFn: async () => fetchConversations(this.rootStore),
    });

    if (
      this.rootStore.personStore.allContacts.length === 0 &&
      !this.rootStore.personStore.loadingContacts
    ) {
      this.rootStore.personStore.getAllContacts();
    }
    if (forceReloadConversations) {
      // Fire-and-forget
      this.rootStore.conversationStore.reloadAndBackfillAllConversations();
    }
    const preloadPromises = [
      this.rootStore.preferenceStore.getExistingPreferenceData(),
      shouldLoadConfig
        ? this.rootStore.configStore.loadConfig()
        : Promise.resolve(),
    ];

    identifyClarityUserId(signedInPerson.id.toString());

    return Promise.all(preloadPromises as any).then(() => {
      this.setIsLoggingIn(false);
      this.rootStore.uiStore.startPresenceReportTick();
      if (!isNullOrUndefined(this.wakeUpCheckTick)) {
        this.wakeUpCheckTick();
        this.wakeUpCheckTick = null;
      }

      intercom.boot(signedInPerson, jwt);

      this.startWakeUpCheckTick();
      this.rootStore.uiStore.loadAllPeoplesPresence();
      void this.rootStore.uiStore.loadAllMessageStatuses();
      if (redirectOnLoaded) {
        const referrer = get(this.rootStore.routerStore.location, 'state.from');
        if (!isEmpty(referrer)) {
          this.rootStore.routerStore.replace(referrer);
        }
      }
    });
  };

  checkPhoneNumberValidation = (phoneNumber: string) => {
    const phoneUtil = PhoneNumberUtil.getInstance();
    const countryCode = phoneNumber.startsWith('+') ? '' : 'US';
    const parsedPhoneNumber = phoneUtil.parseAndKeepRawInput(
      phoneNumber,
      countryCode
    );
    return phoneUtil.isValidNumber(parsedPhoneNumber);
  };

  phoneNumInvalid = (phoneNumber: string) => {
    const regex = new RegExp('[a-zA-Z|@*]');
    return regex.test(phoneNumber);
  };

  removeAllSpecialCaracters = (value: string) => {
    return value
      ? value.replace(/[`~!@#$%^&*()_|\-=?;:'",.<>{}\[\]\\\s/]/gi, '')
      : value;
  };

  @action
  updateProfile = (personId: number, person: any) => {
    try {
      const loadPersonPbo = fromPromise<AxiosResponseT<PersonModel>>(
        API.put(API_ENDPOINTS.Profile, person)
      );

      return loadPersonPbo.then(
        (resp) => {
          this.loadPersonByIdGetSuccess(
            ContactModelV2.fromLoggedPerson(resp.data)
          );
          return this.personsById.get(personId.toString());
        },
        (reason) => {
          this.loadPersonByIdGetFailure(reason, personId.toString());

          return reason;
        }
      );
    } catch (err) {
      console.warn(
        'Failed to load Person ' +
          personId.toString() +
          ' this may be caused by a removed Person, which is not an error.',
        err
      );
    }
  };

  @action
  uploadProfileImgFileToS3 = async (value: { filename: string }) => {
    const s3RespFile = fromPromise(
      API.post(MSG_API_BASE_URI + 'profile/picture', value)
    );
    s3RespFile.then(
      (resp) => {
        if (!this.fileUploadedS3.has(value.filename)) {
          this.fileUploadedS3.set(value.filename, { ...resp.data });
        }
      },
      (reason) =>
        useNotificationStore.getState().addNotify({
          type: 'error',
          dismissible: true,
          autoDismiss: true,
          title: 'Error uploading the profile picture',
          message: 'The profile picture wasn’t updated. Please try again.',
        })
    );
    return s3RespFile;
  };

  uploadImportedProfImageFile = async (file: File) => {
    const fileUrl = this.fileUploadedS3.get(file.name);
    if (!isNullOrUndefined(fileUrl)) {
      //overriding interceptor for axios
      const respFile = fromPromise(
        PURE_API.put(fileUrl.uploadProfilePictureUrl, file, {
          headers: {
            'Content-Type': file.type,
            'Content-Disposition': `attachment; filename=${file.name}`,
          },
        })
      );
      respFile.then(
        () => {},
        (reason) =>
          useNotificationStore.getState().addNotify({
            type: 'error',
            dismissible: true,
            autoDismiss: true,
            title: 'Error uploading the profile picture',
            message: 'The profile picture wasn’t updated. Please try again.',
          })
      );
      return respFile;
    }
  };

  updateProfilePicture = async (file: File) => {
    const fileUrl = this.fileUploadedS3.get(file.name);
    if (!isNullOrUndefined(fileUrl)) {
      return this.updateProfile(this.loggedInPersonId, {
        profilePictureUrl: fileUrl.profilePictureUrl,
      });
    }
  };

  handleUploadToAWS = async (person: PersonStore, files: File[]) => {
    const allS3UploadedFiles = files.map((file) => {
      const fileNameArray = file.name.split('.');
      const extension = [...fileNameArray].pop();
      if (extension === 'jpg') {
        fileNameArray.splice(-1, 1, 'jpeg');
        const name = fileNameArray.join('.');
        return person.uploadProfileImgFileToS3({ filename: name });
      } else {
        const name = fileNameArray.join('.');
        return person.uploadProfileImgFileToS3({ filename: name });
      }
    });

    await Promise.all(allS3UploadedFiles);
    const updateAWSLinks = files.map(async (file) => {
      const extension = file.type.split('/')[1];
      const resizedImage = await resizeImage(file, extension);
      await person.uploadImportedProfImageFile(resizedImage);
      return person.updateProfilePicture(resizedImage);
    });
    return Promise.all(updateAWSLinks);
  };
}

export default PersonStore;

// try to login via jwt if its available
export const retrieveAndClearHashToken = () => {
  const tokenIsInHash = loginTokenIsInHash();
  if (tokenIsInHash) {
    try {
      const hash = queryString.parse(window.location.hash);

      const token: {
        auth?: string;
        refresh?: string;
        expiresAt?: Date;
      } = {};
      token.auth = hash.auth as string;
      token.refresh = hash.refresh as string;

      // https://stackoverflow.com/questions/4631928/convert-utc-epoch-to-local-date
      token.expiresAt = moment.utc(token.expiresAt).toDate();

      // remove #
      window.location.hash = '';

      return typeof token === 'object' && token?.auth ? token.auth : token;
    } catch (err) {
      // remove #
      window.location.hash = '';

      bugsnagClient.notify(
        'Failed to parse authentication token from URL hash',
        (event) => {
          event.severity = 'error';
          event.addMetadata('tokenError', { errorMessage: err.message });
        }
      );

      console.error(err);
      return null;
    }
  }
};
